import React from "react";
import { ICON } from "../../assets";
import { navigate } from "../../routes";
import { admin } from "../../routes/admin";
import { price, round2 } from "../../utils";
import { useAuth } from "../Auth/useAuth";
import useTranslations from "../Translations/useTranslations";
import UiButton from "../ui/UiButton";
import UiIcon from "../ui/UiIcon";
import UiProgressBar from "../ui/UiProgressBar";
import { UIBarColors } from "../ui/UiProgressBar/index";
import "./CurrentCart.css";
import CurrentCartItems from "./CurrentCartItems/CurrentCartItems";
import { useCart } from "./useCart";

function CurrentCart({ disabled_plv_infos = false, ...props }) {
  const [{ user }] = useAuth();
  const [{ current_cart }, cartsMethods] = useCart(user.client_profile?.id);
  const trl = useTranslations();

  const cart_data = cartsMethods.getCartData();
  const max_mt_plv_offert = cartsMethods.getMaxMtPLVOffert(cart_data);
  const mt_plv_in_cart = Math.round(cart_data.cumul_plv_in_cart * 100) / 100;

  const getMtTotal = () => {
    const mt_max_offert = max_mt_plv_offert;
    const mt_total = cart_data.mt_total;
    // Remove packs remise
    const packs = current_cart.packs;

    const toDeduct = packs.reduce(
      (a, b) => (a += b.mt_remise * b.qte_commande),
      0,
    );

    return (
      round2(
        mt_total -
          (mt_plv_in_cart >= mt_max_offert ? mt_max_offert : mt_plv_in_cart),
      ) - toDeduct
    );
  };

  const getFraisPort = () => {
    const { client } = current_cart;
    const mt_port_base = client.groupe_tarifaire.MT_PORT2;
    const franco_port = client.groupe_tarifaire.MT_FRANCO2;

    const total_HT = getMtTotal();

    const frais_port = total_HT >= franco_port ? 0 : mt_port_base;

    return frais_port;
  };

  const thresHoldMinValue = () => {
    const { MT_MINI_COMMANDE } = current_cart.client.groupe_tarifaire;
    return MT_MINI_COMMANDE;
  };
  const thresHoldMaxValue = () => {
    const { client } = current_cart;
    const franco_port = client.groupe_tarifaire.MT_FRANCO2;
    return franco_port;
  };

  const isEmptyCart = () => {
    if (!current_cart) return true;
    const len = Object.keys(current_cart.products).reduce(
      (a, b) => (a += current_cart.products[b].length),
      0,
    );
    return len <= 0;
  };

  function renderOrderActionButton() {
    if (isEmptyCart()) return null;
    const orderValueLimit = thresHoldMinValue();
    const isBelowMinimumOrder =
      orderValueLimit && getMtTotal() < orderValueLimit;

    const isPLVEnabled = !disabled_plv_infos && max_mt_plv_offert > 0;

    const createButton = (
      btn = { label: "", action: () => {}, disabled: false },
    ) => {
      const error = () => {
        if (btn.disabled) {
          return (
            <React.Fragment>
              <div className="order-action-error-message">
                <UiIcon size="md" icon={ICON.errorIcon} />
                Votre commande n’atteint pas le montant minimal de{" "}
                {price(orderValueLimit)} €.
              </div>
            </React.Fragment>
          );
        }
      };
      return (
        <React.Fragment>
          <div className="order-action-container">
            {error()}
            <UiButton
              content={btn.label}
              onClick={btn.action}
              style={{ width: "100%" }}
              disabled={btn.disabled}
            />
          </div>
        </React.Fragment>
      );
    };

    const plvBtn = {
      label: trl.message("Cart.completeOrderAction"),
      action: () => {
        if (cartsMethods.flushSyncCartToDB) {
          cartsMethods.flushSyncCartToDB();
        }
        navigate.push(admin.PLV_PAGE);
      },
      disabled: isBelowMinimumOrder,
    };

    const finishOrderBtn = {
      label: trl.message("Cart.finishOrderAction"),
      action: () => {
        if (cartsMethods.flushSyncCartToDB) {
          cartsMethods.flushSyncCartToDB();
        }
        navigate.push(admin.FINISH_ORDER);
      },
      disabled: isBelowMinimumOrder,
    };

    return (
      <React.Fragment>
        {createButton(isPLVEnabled ? plvBtn : finishOrderBtn)}
      </React.Fragment>
    );
  }

  function renderCartHeaderContent() {
    return (
      <React.Fragment>
        <div className="current-cart-title">Ajouté au panier</div>
        <UiIcon
          size="md"
          onClick={() => props.closeCart()}
          icon={ICON.closeCrossIcon}
        />
      </React.Fragment>
    );
  }

  function renderCartSummaryFooter() {
    return (
      <React.Fragment>
        <div className="current-cart-actions">{renderOrderActionButton()}</div>
      </React.Fragment>
    );
  }

  function renderCartProgressChart() {
    const currentCartValue = getMtTotal();
    const orderValueLimit = thresHoldMinValue();
    const orderValueLimitdMax = thresHoldMaxValue();

    if (orderValueLimit <= 0 || orderValueLimitdMax <= 0) return null;

    const generateStatusMessage = () => {
      let message = "";
      if (currentCartValue < orderValueLimit) {
        message = (
          <React.Fragment>
            Ajouter{" "}
            <span className="cart-highlight-text">
              {price(orderValueLimit - currentCartValue)} €
            </span>{" "}
            pour atteindre le minimum de commande
          </React.Fragment>
        );
      } else if (currentCartValue < orderValueLimitdMax) {
        message = (
          <React.Fragment>
            Ajouter{" "}
            <span className="cart-highlight-text">
              {price(orderValueLimitdMax - currentCartValue)} €
            </span>{" "}
            pour bénéficier des frais de livraison offerts !
          </React.Fragment>
        );
      } else {
        message = "Les frais de livraison vous sont offerts !";
      }
      return (
        <React.Fragment>
          <div className="current-bar-status-message">{message}</div>
        </React.Fragment>
      );
    };
    let targets = [];
    if (orderValueLimit > 0) {
      targets.push({
        value: orderValueLimit,
        offerValue: orderValueLimit,
        label: "commande minimale",
        color: UIBarColors.lightCream,
      });
    }
    if (orderValueLimitdMax > orderValueLimit) {
      targets.push({
        value: orderValueLimitdMax - orderValueLimit,
        offerValue: orderValueLimitdMax,
        label: "frais de livraison offerts",
        color: UIBarColors.brand,
      });
    }
    const exceedsThreshold = (orderValueLimitdMax * 6) / 100;
    targets.push({
      value: exceedsThreshold,
      offerValue: exceedsThreshold,
      label: "",
      color: UIBarColors.brand,
    }); // to ensure the bar fills up to 100% when the value exceeds max threshold

    return (
      <React.Fragment>
        <div className="current-cart-progress-chart">
          {generateStatusMessage()}
          <UiProgressBar
            barConfig={{
              barValue: currentCartValue,
              totalValue: orderValueLimitdMax + exceedsThreshold,
              targets,
            }}
          />
        </div>
      </React.Fragment>
    );
  }

  function renderCurrentCartItems() {
    const renderCartTotal = (fraisPort) => {
      const cartTotal = getMtTotal() + fraisPort; // + getFraisPort() + fraisPort;
      if (cart_data.mt_total > 0) {
        return (
          <React.Fragment>
            <div className="current-cart-total-price">
              <span>Total</span> <span>{price(cartTotal)} €</span>
            </div>
          </React.Fragment>
        );
      }
    };
    const renderExtraInfo = (label, value) => {
      let formattedValue = value;

      if (value <= 0) {
        formattedValue = "offerts !";
      } else {
        formattedValue = price(value) + " €";
      }
      return (
        <React.Fragment>
          <div className="current-cart-fees-item">
            <span className="current-cart-fees-label">{label}</span>
            <span className="current-cart-fees-value">{formattedValue}</span>
          </div>
        </React.Fragment>
      );
    };
    const current_cart_list = Object.keys(current_cart.products);
    const totalAmount = round2(getMtTotal());
    const fraisPort = round2(getFraisPort());

    return (
      <React.Fragment>
        {current_cart_list.length > 0 && (
          <CurrentCartItems hidePriceDetails={true} />
        )}
        <div className="spacer" />
        {renderExtraInfo("Total", totalAmount)}
        {renderExtraInfo("Frais de livraison", fraisPort)}
        <div className="spacer" />
        {renderCartTotal(fraisPort)}
      </React.Fragment>
    );
  }

  function renderCartDetails() {
    if (isEmptyCart()) {
      return (
        <React.Fragment>
          <div className="current-cart-empty">
            {trl.message("Cart.emptyCart")}
          </div>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        {renderCartProgressChart()}
        <div className="spacer mt-4 mb-4" />
        {renderCurrentCartItems()}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="current-cart-container">
        <div className="current-cart-header">{renderCartHeaderContent()}</div>
        <div className="current-cart-content">{renderCartDetails()}</div>
        <div className="current-cart-footer">{renderCartSummaryFooter()}</div>
      </div>
    </React.Fragment>
  );
}

export default CurrentCart;
